import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, ConfigProvider, Form, Modal, Switch, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { dmsAntdTheme } from '../../config/antdTheme';
import {
  generateRtuLicense,
  parseBlobApiError,
  triggerBrowserDownload,
} from '../../services/rtuLicenseService';
import {
  initialRtuLicenseOptions,
  RTU_LICENSE_MODULE_FIELDS,
  RtuLicenseOptions,
} from '../../types/RtuLicense';
import './RtuGenerateLicenceDialog.css';

interface ReadyLicence {
  blob: Blob;
  filename: string;
  sizeBytes: number;
}

const formatLicenceSize = (sizeBytes: number): string => {
  if (sizeBytes > 1000) {
    const kilobytes = sizeBytes / 1024;
    const rounded = kilobytes >= 10 ? kilobytes.toFixed(1) : kilobytes.toFixed(2);
    return `${rounded} KB`;
  }
  return `${sizeBytes} bytes`;
};

const toLicenceFile = (readyLicence: ReadyLicence): File => (
  new File([readyLicence.blob], readyLicence.filename, {
    type: 'application/octet-stream',
    lastModified: Date.now(),
  })
);

interface RtuGenerateLicenceDialogProps {
  visible: boolean;
  rtuId: number | string | null;
  rtuName?: string | null;
  rtuSerial?: string | null;
  /** When true, Upload to RTU is available (serial target resolved). */
  canUploadToRtu?: boolean;
  /** True while the queued upload-licence launch is in flight. */
  uploadingToRtu?: boolean;
  onHide: () => void;
  /** Launch UPDATE_LICENSE with the generated .lic without a separate file picker. */
  onUploadToRtu?: (file: File) => void | Promise<boolean | void>;
}

const RtuGenerateLicenceDialog = ({
  visible,
  rtuId,
  rtuName,
  rtuSerial,
  canUploadToRtu = false,
  uploadingToRtu = false,
  onHide,
  onUploadToRtu,
}: RtuGenerateLicenceDialogProps) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<RtuLicenseOptions>(initialRtuLicenseOptions);
  const [isGenerating, setIsGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [readyLicence, setReadyLicence] = useState<ReadyLicence | null>(null);

  const busy = isGenerating || uploadingToRtu;

  useEffect(() => {
    if (!visible) {
      return;
    }
    setOptions(initialRtuLicenseOptions);
    setErrorMessage(null);
    setIsGenerating(false);
    setReadyLicence(null);
  }, [visible, rtuId]);

  const title = useMemo(() => t('rtuActions.license.dialogTitle'), [t]);
  const showUploadAction = Boolean(readyLicence && onUploadToRtu);

  const onToggle = (key: keyof RtuLicenseOptions, checked: boolean) => {
    setOptions((current) => ({ ...current, [key]: checked }));
    setReadyLicence(null);
  };

  const handleGenerate = async () => {
    if (busy || rtuId == null) {
      return;
    }
    setIsGenerating(true);
    setErrorMessage(null);
    setReadyLicence(null);
    try {
      const fallbackFilename = rtuSerial ? `${rtuSerial}.lic` : undefined;
      const result = await generateRtuLicense(rtuId, options, fallbackFilename);
      setReadyLicence({
        blob: result.blob,
        filename: result.filename,
        sizeBytes: result.blob.size,
      });
    } catch (error) {
      const parsed = await parseBlobApiError(error, t('rtuActions.license.failed'));
      setErrorMessage(parsed);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!readyLicence || busy) {
      return;
    }
    triggerBrowserDownload(readyLicence.blob, readyLicence.filename);
  };

  const handleUploadToRtu = () => {
    if (!readyLicence || !onUploadToRtu || !canUploadToRtu || busy) {
      return;
    }
    void onUploadToRtu(toLicenceFile(readyLicence));
  };

  return (
    <ConfigProvider theme={dmsAntdTheme}>
      <Modal
        open={visible}
        title={title}
        onCancel={busy ? undefined : onHide}
        footer={null}
        destroyOnHidden
        centered={false}
        style={{ top: 72 }}
        width={560}
        className="rtu-generate-licence-dialog"
        closable={!busy}
        maskClosable={!busy}
        keyboard={!busy}
      >
        <Form layout="vertical" disabled={busy}>
          <Form.Item label={t('rtuActions.license.rtuName')}>
            <Typography.Text>{rtuName || '-'}</Typography.Text>
          </Form.Item>
          <Form.Item label={t('rtuActions.license.rtuSerial')}>
            <Typography.Text code>{rtuSerial || '-'}</Typography.Text>
          </Form.Item>
          <Typography.Title level={5}>{t('rtuActions.license.modulesTitle')}</Typography.Title>
          <div className="rtu-generate-licence-dialog__modules">
            {RTU_LICENSE_MODULE_FIELDS.map((field) => (
              <div key={field.key} className="rtu-generate-licence-dialog__module-row">
                <span>{t(field.labelKey)}</span>
                <Switch
                  checked={options[field.key]}
                  disabled={busy}
                  onChange={(checked) => onToggle(field.key, checked)}
                  aria-label={t(field.labelKey)}
                />
              </div>
            ))}
          </div>
          {errorMessage ? <Alert type="error" showIcon message={errorMessage} style={{ marginTop: 16 }} /> : null}
          {readyLicence ? (
            <Alert
              type="success"
              showIcon
              style={{ marginTop: 16 }}
              message={t('rtuActions.license.readyTitle')}
              description={t('rtuActions.license.readyDescription', {
                filename: readyLicence.filename,
                size: formatLicenceSize(readyLicence.sizeBytes),
              })}
            />
          ) : null}
          <div className="rtu-generate-licence-dialog__actions">
            <Button onClick={onHide} disabled={busy}>
              {t('rtuActions.license.cancel')}
            </Button>
            <Button
              type={readyLicence ? 'default' : 'primary'}
              loading={isGenerating}
              disabled={busy || rtuId == null}
              onClick={() => void handleGenerate()}
            >
              {isGenerating
                ? t('rtuActions.license.generating')
                : readyLicence
                  ? t('rtuActions.license.regenerate')
                  : t('rtuActions.license.generate')}
            </Button>
            {readyLicence ? (
              <Button onClick={handleDownload} disabled={busy}>
                {t('rtuActions.license.download')}
              </Button>
            ) : null}
            {showUploadAction ? (
              <Button
                type="primary"
                className="rtu-generate-licence-dialog__upload"
                loading={uploadingToRtu}
                disabled={busy || !canUploadToRtu}
                title={!canUploadToRtu ? t('rtuActions.license.uploadUnavailable') : undefined}
                onClick={handleUploadToRtu}
              >
                {uploadingToRtu
                  ? t('rtuActions.license.uploading')
                  : t('rtuActions.license.uploadToRtu')}
              </Button>
            ) : null}
          </div>
        </Form>
      </Modal>
    </ConfigProvider>
  );
};

export default RtuGenerateLicenceDialog;
